import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignaturePad from "../../components/SignaturePad";
import { driverApi } from "../../services/driverApi";

const DOCS_WITH_FORMS = ["record_of_violation"];
const DEDICATED_PAGE_ROUTES = {
  new_driver_application: { path: "/enrollme/driver/application", cta: "Fill Out & Sign Application →", desc: "You'll fill in your employment history, driving experience, and accident record. Most fields are pre-filled from your profile." },
  independent_contractor_agreement: { path: "/enrollme/driver/ica", cta: "Review & Sign Agreement →", desc: "Confirm your information, initial each policy section, then sign the agreement. Your info is pre-filled from your profile." },
};

const emptyViolation = () => ({ date: "", offense: "", location: "", vehicleType: "" });

export default function SignDocuments({ driver }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [downloadOnlyDocs, setDownloadOnlyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [showPad, setShowPad] = useState(false);
  const [signerName, setSignerName] = useState(`${driver?.firstName || ""} ${driver?.lastName || ""}`.trim());
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Record of Violation inline form state
  const [rovNoViolations, setRovNoViolations] = useState(true);
  const [rovViolations, setRovViolations] = useState([emptyViolation()]);
  const [rovStep, setRovStep] = useState("form"); // "form" | "sign"

  async function load() {
    setLoading(true);
    try {
      const { docs: d, downloadOnlyDocs: dl } = await driverApi.getDocuments();
      setDocs(d || []);
      setDownloadOnlyDocs(dl || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleSign({ signatureType, signatureData }) {
    if (!signerName.trim()) { setError("Please enter your full legal name."); return; }
    setSigning(true);
    setError("");
    try {
      const payload = { documentId: active.id, signerName, signatureType, signatureData };
      if (active.id === "record_of_violation") {
        payload.formData = {
          noViolations: rovNoViolations,
          violations: rovViolations,
          driverName: `${driver?.lastName || ""}, ${driver?.firstName || ""}`,
        };
      }
      await driverApi.signDocument(payload);
      setShowPad(false);
      setRovStep("form");
      setActive(null);
      setSuccess(`"${active.label}" signed successfully.`);
      setTimeout(() => setSuccess(""), 4000);
      load();
    } catch (err) { setError(err.message); }
    finally { setSigning(false); }
  }

  async function handleDownloadSigned(docId, label) {
    try { const { link } = await driverApi.downloadSignedDoc(docId); window.open(link, "_blank"); }
    catch (err) { setError(`Could not download ${label}.`); }
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  const allSigned = docs.every((d) => !d.requiresSignature || d.signed);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Documents & Forms</h1>
        <p className="text-sm text-gray-500 mt-1">
          Read each document, review the key points, then apply your signature. You can download a signed copy after signing.
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-sm">{success}</div>}

      {/* ── Documents that require signature ── */}
      <div className="space-y-3 mb-8">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Sign Required Documents</p>

        {docs.map((doc) => (
          <div key={doc.id} className={`bg-white rounded-xl border overflow-hidden transition ${doc.signed ? "border-green-200" : "border-gray-200"}`}>
            {/* Row header */}
            <div className="px-5 py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${doc.signed ? "bg-green-100" : "bg-gray-100"}`}>
                  {doc.signed ? (
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{doc.label}</p>
                  {doc.signed
                    ? <p className="text-xs text-green-600">Signed {new Date(doc.signedAt).toLocaleDateString()}</p>
                    : <p className="text-xs text-gray-400">Signature required</p>}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {doc.documentFile && (
                  <a href={doc.documentFile} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline font-medium border border-blue-200 px-2.5 py-1 rounded-lg">
                    View Document
                  </a>
                )}
                {doc.signed ? (
                  <button onClick={() => handleDownloadSigned(doc.id, doc.label)}
                    className="text-xs text-green-700 hover:underline font-medium border border-green-200 px-2.5 py-1 rounded-lg">
                    Download Signed
                  </button>
                ) : (
                  <button
                    onClick={() => { setActive(active?.id === doc.id ? null : doc); setShowPad(false); }}
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 font-medium">
                    {active?.id === doc.id ? "Collapse" : "Review & Sign"}
                  </button>
                )}
              </div>
            </div>

            {/* Expanded panel */}
            {active?.id === doc.id && (
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-5">

                {/* ── Dedicated-page docs (ICA, New Driver Application) ── */}
                {doc.id in DEDICATED_PAGE_ROUTES && (() => {
                  const route = DEDICATED_PAGE_ROUTES[doc.id];
                  return (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <div>
                          <p className="text-sm text-amber-800 font-medium">This document requires a guided form</p>
                          <p className="text-xs text-amber-700 mt-0.5">{route.desc}</p>
                        </div>
                      </div>
                      {doc.documentFile && (
                        <a href={doc.documentFile} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          Read the full document ↗
                        </a>
                      )}
                      <button onClick={() => navigate(route.path)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg text-sm transition">
                        {route.cta}
                      </button>
                      <button onClick={() => setActive(null)} className="w-full text-sm text-gray-500 hover:text-gray-700 py-1">Cancel</button>
                    </div>
                  );
                })()}

                {/* ── Record of Violation inline form ── */}
                {doc.id === "record_of_violation" && (
                  <div>
                    {rovStep === "form" && (
                      <div className="space-y-4">
                        <p className="text-sm font-semibold text-gray-700">Motor Vehicle Driver's Certification of Violations</p>
                        <p className="text-xs text-gray-500">List all traffic violations (other than parking) for which you have been convicted or forfeited bond in the past 12 months.</p>

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={rovNoViolations} onChange={(e) => setRovNoViolations(e.target.checked)} className="accent-blue-600 w-4 h-4" />
                          <span className="font-medium text-gray-700">I have no violations to report in the past 12 months</span>
                        </label>

                        {!rovNoViolations && (
                          <div className="space-y-3">
                            {rovViolations.map((v, i) => (
                              <div key={i} className="space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                                <p className="text-xs font-medium text-gray-500">Violation {i + 1}</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Date of Conviction</p>
                                    <input value={v.date} onChange={(e) => setRovViolations((a) => a.map((r, idx) => idx === i ? { ...r, date: e.target.value } : r))}
                                      placeholder="MM/DD/YYYY" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Type of Vehicle</p>
                                    <input value={v.vehicleType} onChange={(e) => setRovViolations((a) => a.map((r, idx) => idx === i ? { ...r, vehicleType: e.target.value } : r))}
                                      placeholder="e.g. Sedan" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Offense</p>
                                  <input value={v.offense} onChange={(e) => setRovViolations((a) => a.map((r, idx) => idx === i ? { ...r, offense: e.target.value } : r))}
                                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Location</p>
                                  <input value={v.location} onChange={(e) => setRovViolations((a) => a.map((r, idx) => idx === i ? { ...r, location: e.target.value } : r))}
                                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </div>
                              </div>
                            ))}
                            {rovViolations.length < 5 && (
                              <button type="button" onClick={() => setRovViolations((a) => [...a, emptyViolation()])}
                                className="text-xs text-blue-600 hover:underline font-medium">+ Add another violation</button>
                            )}
                          </div>
                        )}

                        <button onClick={() => setRovStep("sign")}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg text-sm transition">
                          Continue — Apply Signature →
                        </button>
                        <button onClick={() => setActive(null)} className="w-full text-sm text-gray-500 hover:text-gray-700 py-1">Cancel</button>
                      </div>
                    )}

                    {rovStep === "sign" && (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-blue-800">
                            {rovNoViolations ? "Certifying: no violations in past 12 months" : `Certifying: ${rovViolations.filter((v) => v.offense).length} violation(s) listed`}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Your Full Legal Name</label>
                          <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Type your full legal name" />
                        </div>
                        <SignaturePad onSave={handleSign} onCancel={() => setRovStep("form")} />
                        {signing && <p className="text-sm text-center text-gray-500 mt-3">Generating signed document…</p>}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Standard docs (lease, violation penalty, etc.) ── */}
                {!(doc.id in DEDICATED_PAGE_ROUTES) && doc.id !== "record_of_violation" && (
                  <div>
                    {doc.documentFile && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-3">
                        <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <div>
                          <p className="text-sm text-blue-800 font-medium">Read the full document before signing</p>
                          <a href={doc.documentFile} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                            Open {doc.label} ↗
                          </a>
                        </div>
                      </div>
                    )}
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Key Points — I understand and agree that:</h3>
                    <ul className="space-y-2 mb-5">
                      {doc.keyHighlights.map((h, i) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-700">
                          <span className="text-blue-500 font-bold mt-0.5 flex-shrink-0">·</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    {!showPad ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Your Full Legal Name</label>
                          <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Type your full legal name exactly as it appears on your ID" />
                        </div>
                        <button onClick={() => setShowPad(true)} disabled={!signerName.trim()}
                          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg text-sm transition">
                          I Have Read This Document — Proceed to Sign
                        </button>
                        <button onClick={() => setActive(null)} className="w-full text-sm text-gray-500 hover:text-gray-700 py-1">Cancel</button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-3">Apply your signature below</p>
                        <SignaturePad onSave={handleSign} onCancel={() => setShowPad(false)} />
                        {signing && <p className="text-sm text-center text-gray-500 mt-3">Saving signature…</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Download-only documents ── */}
      {downloadOnlyDocs.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Reference Documents — Download &amp; Keep</p>
          <div className="space-y-2">
            {downloadOnlyDocs.map((doc) => (
              <div key={doc.id} className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{doc.description}</p>
                </div>
                <a href={doc.documentFile} target="_blank" rel="noopener noreferrer" download
                  className="flex-shrink-0 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All done */}
      {allSigned && docs.length > 0 && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5 text-center">
          <p className="text-green-700 font-semibold">All required documents signed!</p>
          <p className="text-sm text-green-600 mt-1">Next: complete the WC43 form and upload your compliance documents.</p>
        </div>
      )}
    </div>
  );
}
